/**
 * CfeSettingsController — onglet « CFE » de /profil-entreprise.html (EPIC-024 / 24.4).
 *
 * Pilote 1 nm-edit-block avec 3 champs (montant CFE année N-1, dernière année
 * payée, toggle opt-in pour l'extension d'exonération N+1) et le résumé.
 *
 * Convention CLAUDE.md respectée :
 *   - Pas d'HTML inline en JS : le résumé est rendu depuis <template id="tpl-cfe-summary">
 *     cloné via cloneTemplate / fillSlots.
 *   - Aucune classe CSS inventée.
 *   - Visibilité du toggle « exonération étendue N+1 » : se fie au flag
 *     `firstYearExonerationActive` retourné par GET /cfe/overview (source
 *     de vérité backend, aucun recalcul JS).
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const AMOUNT_FR_REGEX = /^\d+(?:[.,]\d{1,2})?$/;
const AMOUNT_MIN = 0;
const AMOUNT_MAX = 999999.99;
const YEAR_MIN = 2000;
const YEAR_MAX = 2100;

const AMOUNT_FORMATTER = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export default class CfeSettingsController extends BaseController {
  static targets = [
    "block",
    "summary",
    "exonerationToggle",
    "amountInput",
    "yearInput",
    "exonerationCheckbox",
    "submit",
  ];

  state = {
    cfeAmountLastYear: null,
    cfePaidForYear: null,
    cfeFirstYearExonerationExtendedEnabled: false,
    firstYearExonerationActive: false,
  };

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  async connect() {
    try {
      const data = await api.getCfeOverview();
      this._hydrate(data);
    } catch (error) {
      // Hydratation silencieuse : panel reste en placeholder si l'API n'est
      // pas encore livrée (24.2 / 24.3 pending) ou injoignable.
      console.warn("[cfe-settings] hydration failed", error);
    }
  }

  // ---------------------------------------------------------------------------
  // edit-block : open / cancel
  // ---------------------------------------------------------------------------

  openBlock(event) {
    event.preventDefault();
    const block = this.hasBlockTarget ? this.blockTarget : null;
    if (!block) return;
    this._restoreInputs();
    this._updateToggleVisibility();
    this._open(block);
  }

  cancelBlock(event) {
    event.preventDefault();
    const block = this.hasBlockTarget ? this.blockTarget : null;
    if (!block) return;
    this._restoreInputs();
    this._close(block);
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async submit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const payload = this._readForm();
    if (payload === null) return;

    if (this.hasSubmitTarget) this.submitTarget.disabled = true;
    try {
      const data = await api.updateCfeSettings(payload);
      this._hydrate(data);
      const block = this.hasBlockTarget ? this.blockTarget : null;
      if (block) this._close(block);
      this.showSuccess("Données CFE enregistrées.");
    } catch (error) {
      const msg = error && error.message
        ? error.message
        : "Erreur lors de l'enregistrement des données CFE.";
      this.showError(msg);
    } finally {
      if (this.hasSubmitTarget) this.submitTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Hydration
  // ---------------------------------------------------------------------------

  _hydrate(data) {
    if (!data || typeof data !== "object") return;
    if (Object.hasOwn(data, "cfeAmountLastYear")) {
      this.state.cfeAmountLastYear = this._toNumberOrNull(data.cfeAmountLastYear);
    }
    if (Object.hasOwn(data, "cfePaidForYear")) {
      this.state.cfePaidForYear = this._toIntOrNull(data.cfePaidForYear);
    }
    if (Object.hasOwn(data, "cfeFirstYearExonerationExtendedEnabled")) {
      this.state.cfeFirstYearExonerationExtendedEnabled = Boolean(data.cfeFirstYearExonerationExtendedEnabled);
    }
    if (Object.hasOwn(data, "firstYearExonerationActive")) {
      this.state.firstYearExonerationActive = Boolean(data.firstYearExonerationActive);
    }
    this._renderSummary();
    this._restoreInputs();
    this._updateToggleVisibility();
  }

  _renderSummary() {
    if (!this.hasSummaryTarget) return;
    const fragment = this.cloneTemplate("tpl-cfe-summary");
    if (!fragment) return;
    this.fillSlots(fragment, {
      amount: this.state.cfeAmountLastYear !== null
        ? `${AMOUNT_FORMATTER.format(this.state.cfeAmountLastYear)} €`
        : "—",
      paidForYear: this.state.cfePaidForYear !== null
        ? String(this.state.cfePaidForYear)
        : "—",
    });
    this._replaceChildren(this.summaryTarget, fragment);
  }

  _restoreInputs() {
    if (this.hasAmountInputTarget) {
      this.amountInputTarget.value = this.state.cfeAmountLastYear !== null
        ? AMOUNT_FORMATTER.format(this.state.cfeAmountLastYear)
        : "";
    }
    if (this.hasYearInputTarget) {
      this.yearInputTarget.value = this.state.cfePaidForYear !== null
        ? String(this.state.cfePaidForYear)
        : "";
    }
    if (this.hasExonerationCheckboxTarget) {
      this.exonerationCheckboxTarget.checked = this.state.cfeFirstYearExonerationExtendedEnabled;
    }
  }

  _updateToggleVisibility() {
    if (!this.hasExonerationToggleTarget) return;
    this.exonerationToggleTarget.hidden = !this.state.firstYearExonerationActive;
  }

  // ---------------------------------------------------------------------------
  // Form parsing + validation
  // ---------------------------------------------------------------------------

  _readForm() {
    const payload = {};

    const rawAmount = this.hasAmountInputTarget ? this.amountInputTarget.value.trim() : "";
    if (rawAmount === "") {
      payload.cfeAmountLastYear = null;
    } else {
      const amount = this._parseAmountFr(rawAmount);
      if (amount === null) {
        this.showError("Montant CFE invalide. Format attendu : 1234,56 (max 2 décimales).");
        return null;
      }
      if (amount < AMOUNT_MIN || amount > AMOUNT_MAX) {
        this.showError(`Montant CFE hors plage : ${AMOUNT_MIN} € à ${AMOUNT_FORMATTER.format(AMOUNT_MAX)} €.`);
        return null;
      }
      payload.cfeAmountLastYear = amount;
    }

    const rawYear = this.hasYearInputTarget ? this.yearInputTarget.value.trim() : "";
    if (rawYear === "") {
      payload.cfePaidForYear = null;
    } else {
      const year = Number.parseInt(rawYear, 10);
      if (!Number.isInteger(year) || String(year) !== rawYear) {
        this.showError("Année invalide. Entier attendu (ex. 2025).");
        return null;
      }
      if (year < YEAR_MIN || year > YEAR_MAX) {
        this.showError(`Année hors plage : ${YEAR_MIN} à ${YEAR_MAX}.`);
        return null;
      }
      payload.cfePaidForYear = year;
    }

    if (this.hasExonerationCheckboxTarget && this.state.firstYearExonerationActive) {
      payload.cfeFirstYearExonerationExtendedEnabled = this.exonerationCheckboxTarget.checked;
    }

    return payload;
  }

  _parseAmountFr(raw) {
    const normalized = raw.replace(/[\s ]/g, "").replace(",", ".");
    if (!AMOUNT_FR_REGEX.test(normalized)) return null;
    const value = Number.parseFloat(normalized);
    return Number.isFinite(value) ? value : null;
  }

  _toNumberOrNull(value) {
    if (value === null || value === undefined || value === "") return null;
    const n = typeof value === "number" ? value : Number.parseFloat(value);
    return Number.isFinite(n) ? n : null;
  }

  _toIntOrNull(value) {
    if (value === null || value === undefined || value === "") return null;
    const n = typeof value === "number" ? value : Number.parseInt(value, 10);
    return Number.isInteger(n) ? n : null;
  }

  // ---------------------------------------------------------------------------
  // edit-block open/close (pattern partagé profile_entreprise_bank_controller)
  // ---------------------------------------------------------------------------

  _open(block) {
    block.dataset.editState = "editing";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (summary) summary.hidden = true;
    if (form) {
      form.hidden = false;
      const first = form.querySelector("input:not([type='hidden']), select, textarea");
      if (first) first.focus();
    }
  }

  _close(block) {
    block.dataset.editState = "idle";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (form) form.hidden = true;
    if (summary) summary.hidden = false;
  }

  _replaceChildren(target, fragment) {
    while (target.firstChild) target.removeChild(target.firstChild);
    target.appendChild(fragment);
  }
}
